import { useState, useEffect, createContext, useContext } from 'react'
import type { ElementMailbox } from '../types'
import {
  loginToElementMail,
  logoutOfElementMail,
  fetchAuthenticatedMailbox
} from '../api'

export interface ElementAuthState {
  authenticatedMailbox: ElementMailbox | null
  storedAuthToken: string | null
  isAuthenticationLoading: boolean
  authenticationError: string | null
}

export interface ElementAuthActions {
  performElementLogin: (emailAddress: string, plaintextPassword: string) => Promise<void>
  performElementLogout: () => Promise<void>
  clearAuthenticationError: () => void
}

export type ElementAuthContextValue = ElementAuthState & ElementAuthActions

const ElementAuthContext = createContext<ElementAuthContextValue | null>(null)

export function useElementAuth(): ElementAuthContextValue {
  const authContextValue = useContext(ElementAuthContext)
  if (!authContextValue) {
    throw new Error('useElementAuth must be used inside ElementAuthProvider')
  }
  return authContextValue
}

export function useElementAuthProvider(): ElementAuthContextValue {
  const [authenticatedMailbox, setAuthenticatedMailbox] = useState<ElementMailbox | null>(null)
  const [storedAuthToken, setStoredAuthToken] = useState<string | null>(
    localStorage.getItem('element_auth_token')
  )
  const [isAuthenticationLoading, setIsAuthenticationLoading] = useState<boolean>(true)
  const [authenticationError, setAuthenticationError] = useState<string | null>(null)

  useEffect(() => {
    async function restoreElementAuthSession(): Promise<void> {
      const persistedToken = localStorage.getItem('element_auth_token')
      if (!persistedToken) {
        setIsAuthenticationLoading(false)
        return
      }
      try {
        const restoredMailbox = await fetchAuthenticatedMailbox()
        setAuthenticatedMailbox(restoredMailbox)
        setStoredAuthToken(persistedToken)
      } catch {
        localStorage.removeItem('element_auth_token')
        setStoredAuthToken(null)
      } finally {
        setIsAuthenticationLoading(false)
      }
    }
    restoreElementAuthSession()
  }, [])

  async function performElementLogin(
    emailAddress: string,
    plaintextPassword: string
  ): Promise<void> {
    setIsAuthenticationLoading(true)
    setAuthenticationError(null)
    try {
      const loginResponse = await loginToElementMail(emailAddress, plaintextPassword)
      localStorage.setItem('element_auth_token', loginResponse.token)
      setStoredAuthToken(loginResponse.token)
      setAuthenticatedMailbox({
        id: loginResponse.mailbox.id,
        email: loginResponse.mailbox.email,
        displayName: loginResponse.mailbox.displayName,
        quota: { totalBytes: 0, usedBytes: 0 }
      })
      const fullMailboxRecord = await fetchAuthenticatedMailbox()
      setAuthenticatedMailbox(fullMailboxRecord)
    } catch (loginError: unknown) {
      const errorMessage = loginError instanceof Error ? loginError.message : 'Login failed'
      setAuthenticationError(errorMessage)
      throw loginError
    } finally {
      setIsAuthenticationLoading(false)
    }
  }

  async function performElementLogout(): Promise<void> {
    try {
      await logoutOfElementMail()
    } finally {
      setAuthenticatedMailbox(null)
      setStoredAuthToken(null)
      localStorage.removeItem('element_auth_token')
    }
  }

  function clearAuthenticationError(): void {
    setAuthenticationError(null)
  }

  return {
    authenticatedMailbox,
    storedAuthToken,
    isAuthenticationLoading,
    authenticationError,
    performElementLogin,
    performElementLogout,
    clearAuthenticationError
  }
}

export { ElementAuthContext }
